import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface Publisher {
  publisher_id: string
  slug: string
  display_name: string
  type: 'individual' | 'organization'
  email: string
  verified: boolean
  website_url?: string
}

interface PublisherAuthContextType {
  publisher: Publisher | null
  token: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (data: SignupData) => Promise<void>
  logout: () => void
  loading: boolean
}

interface SignupData {
  email: string
  password: string
  display_name: string
  type: 'individual' | 'organization'
  website_url?: string
}

const PublisherAuthContext = createContext<PublisherAuthContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export function PublisherAuthProvider({ children }: { children: ReactNode }) {
  const [publisher, setPublisher] = useState<Publisher | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const storedToken = localStorage.getItem('xarlie_publisher_token')
    const storedPublisher = localStorage.getItem('xarlie_publisher')

    if (storedToken && storedPublisher) {
      try {
        setToken(storedToken)
        setPublisher(JSON.parse(storedPublisher))
      } catch (e) {
        console.error('Failed to parse stored publisher:', e)
        localStorage.removeItem('xarlie_publisher_token')
        localStorage.removeItem('xarlie_publisher')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    console.log('[PublisherAuth] Attempting login for:', email)

    const response = await fetch(`${API_BASE_URL}/v1/publisher/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }))
      console.error('[PublisherAuth] Login failed:', error)
      throw new Error(error.message || 'Invalid credentials')
    }

    const data = await response.json()
    console.log('[PublisherAuth] Login successful')

    setToken(data.token)
    setPublisher(data.publisher)

    localStorage.setItem('xarlie_publisher_token', data.token)
    localStorage.setItem('xarlie_publisher', JSON.stringify(data.publisher))
  }

  const signup = async (data: SignupData) => {
    console.log('[PublisherAuth] Attempting signup for:', data.email)

    const response = await fetch(`${API_BASE_URL}/v1/publisher/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Signup failed' }))
      console.error('[PublisherAuth] Signup failed:', error)
      throw new Error(error.message || 'Signup failed')
    }

    const result = await response.json()
    console.log('[PublisherAuth] Signup successful')

    setToken(result.token)
    setPublisher(result.publisher)

    localStorage.setItem('xarlie_publisher_token', result.token)
    localStorage.setItem('xarlie_publisher', JSON.stringify(result.publisher))
  }

  const logout = () => {
    console.log('[PublisherAuth] Logging out')
    setToken(null)
    setPublisher(null)
    localStorage.removeItem('xarlie_publisher_token')
    localStorage.removeItem('xarlie_publisher')
  }

  const value: PublisherAuthContextType = {
    publisher,
    token,
    isAuthenticated: !!token,
    login,
    signup,
    logout,
    loading,
  }

  return (
    <PublisherAuthContext.Provider value={value}>
      {children}
    </PublisherAuthContext.Provider>
  )
}

export function usePublisherAuth() {
  const context = useContext(PublisherAuthContext)
  if (context === undefined) {
    throw new Error('usePublisherAuth must be used within a PublisherAuthProvider')
  }
  return context
}
